import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Newspaper, Tag, Sun, Moon, ArrowLeft } from 'lucide-react';

const fontStyle = document.createElement('style');
fontStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
`;
document.head.appendChild(fontStyle);

const AboutPage = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/title.png" alt="CivicDuty Logo" className="h-10 w-auto cursor-pointer" style={{maxHeight: '3.5rem'}} onClick={() => navigate('/')} />
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-x-1"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                <span>Back to Home</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className="text-center mb-12">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-red-100 to-blue-100'} mb-6`}>
              <Users className={`w-8 h-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
            </div>
            <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Our Mission</h1>
            <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
              Empowering citizens with transparent access to their representatives' activities and policy positions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-red-50 to-blue-50'} rounded-xl p-6`}>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Transparency</h2>
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                We believe in making government more accessible and transparent. Our platform provides easy access to your representatives' activities, policy positions, and legislative updates.
              </p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-red-50'} rounded-xl p-6`}>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Engagement</h2>
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                Stay informed about the decisions that affect your community. Track press releases, policy changes, and legislative activities in real-time.
              </p>
            </div>
          </div>
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-8 mb-12`}>
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-6 text-center`}>What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className={`w-12 h-12 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Search className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Easy Search</h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Find your representatives quickly with our intuitive search interface</p>
              </div>
              <div className="text-center">
                <div className={`w-12 h-12 ${isDarkMode ? 'bg-red-900' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Newspaper className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Press Updates</h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Stay updated with the latest press releases and announcements</p>
              </div>
              <div className="text-center">
                <div className={`w-12 h-12 ${isDarkMode ? 'bg-purple-900' : 'bg-purple-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Tag className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Policy Tags</h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Track policy positions and legislative activities by topic</p>
              </div>
            </div>
          </div>
          <div className="text-center">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white font-semibold rounded-lg hover:from-red-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Return to Homepage
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AboutPage; 